// ═══════════════════════════════════════════════════════════════════════
// Temps › Réglages — éléments communs aux trois écrans (horaires, jours fériés, paramètres).
// Les droits affichés viennent du serveur (/api/temps/referentiels/droits) : jamais d'une liste
// de rôles écrite ici. Le serveur refuse de toute façon ce qui n'est pas permis.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, CalendarDays, SlidersHorizontal, Tablet, X } from 'lucide-react';
import { Badge, Bouton } from '../../design/composants';
// Menu « Temps » et droits effectifs (agent I) : la barre « Réglages » s'affiche sous le menu Temps.
import { NavTemps, useDroitsTemps } from './rhCommun';

const API = '/api/temps/referentiels';

export const lire = async (url, params) => (await axios.get(`${API}${url}`, { params })).data.data;
export const ecrire = async (methode, url, corps) => (await axios({ method: methode, url: `${API}${url}`, data: corps })).data.data;
export const messageErreur = (e, defaut = 'Opération impossible. Réessayez.') => e?.response?.data?.message || defaut;

/** Droits effectifs et listes de choix (sites, services, départements, postes, salariés). */
export function useReglages() {
  const [droits, setDroits] = useState(null);
  const [options, setOptions] = useState({ sites: [], services: [], departements: [], postes: [], employes: [] });
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    let actif = true;
    (async () => {
      try {
        const d = await lire('/droits');
        if (!actif) return;
        setDroits(d);
        if (d.lecture) { const o = await lire('/options'); if (actif) setOptions(o); }
      } catch (e) { if (actif) setErreur(messageErreur(e, 'Chargement des droits impossible.')); }
    })();
    return () => { actif = false; };
  }, []);
  return { droits, options, erreur };
}

/** Navigation entre les écrans de réglages (onglets-liens), selon les droits effectifs du serveur. */
export function NavReglages({ actif }) {
  const droits = useDroitsTemps();
  const lecture = !droits || !!droits.reglages?.lecture;
  const liens = [
    ...(lecture ? [
      { cle: 'horaires', vers: '/temps/reglages/horaires', libelle: 'Horaires', Icone: Clock },
      { cle: 'feries', vers: '/temps/reglages/feries', libelle: 'Jours fériés', Icone: CalendarDays },
      { cle: 'parametres', vers: '/parametres/temps', libelle: 'Règles de calcul (Paramètres)', Icone: SlidersHorizontal },
    ] : []),
    ...(droits?.pointage?.gerer_bornes ? [{ cle: 'bornes', vers: '/temps/reglages/bornes', libelle: 'Bornes', Icone: Tablet }] : []),
  ];
  return (
    <>
    <NavTemps actif="reglages" />
    <nav className="rh-onglets" aria-label="Réglages du temps">
      {liens.map((l) => (
        <Link key={l.cle} to={l.vers} className="rh-onglet" aria-current={actif === l.cle ? 'page' : undefined}
          style={{ textDecoration: 'none', ...(actif === l.cle ? { borderBottomColor: 'var(--sg-primaire)', color: 'var(--sg-primaire)', fontWeight: 500 } : {}) }}>
          <l.Icone size={15} aria-hidden="true" />{l.libelle}
        </Link>
      ))}
    </nav>
    </>
  );
}

/** Fenêtre modale simple (Échap ferme). */
export function Fenetre({ titre, Icone, onFermer, children, large = false }) {
  const [fermer] = useState(() => onFermer);
  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape') fermer(); };
    document.addEventListener('keydown', echap);
    return () => document.removeEventListener('keydown', echap);
  }, [fermer]);
  return (
    <div role="dialog" aria-modal="true" aria-label={titre}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div className="rh-carte" style={{ width: large ? 900 : 520, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0 }}>
        <div className="rh-carte-entete">
          <h2 className="rh-carte-titre">{Icone && <Icone size={17} aria-hidden="true" />}{titre}</h2>
          <Bouton variante="discret" className="rh-btn-icone" Icone={X} onClick={onFermer} aria-label="Fermer" />
        </div>
        {children}
      </div>
    </div>
  );
}

/** Champ de formulaire : libellé au-dessus, aide en dessous. */
export function Champ({ libelle, requis, aide, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 12, minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>
        {libelle}{requis && <span className="rh-form-requis" aria-hidden="true">*</span>}
      </span>
      {children}
      {aide && <span className="rh-form-aide" style={{ display: 'block' }}>{aide}</span>}
    </label>
  );
}

export const PiedFenetre = ({ children }) => <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>{children}</div>;

/** Hook de chargement avec rechargement explicite. */
export function useChargement(charger, deps) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const recharger = useCallback(async () => {
    try { setErreur(''); setDonnees(await charger()); } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); setDonnees(null); }
  }, deps);
  useEffect(() => { recharger(); }, [recharger]);
  return { donnees, erreur, recharger };
}

// ─── Libellés ────────────────────────────────────────────────────────────────────────────────
const STATUTS = {
  A_VALIDER: ['alerte', 'À valider'], BROUILLON: ['alerte', 'À valider'], VALIDE: ['succes', 'Validé'], ACTIF: ['succes', 'En vigueur'],
  PROGRAMME: ['info', 'Programmé'], ARCHIVE: ['neutre', 'Archivé'], ANNULE: ['neutre', 'Annulé'], ANNULEE: ['neutre', 'Annulée'], ACTIVE: ['succes', 'En cours'],
};
export function BadgeStatut({ statut }) {
  const [ton, libelle] = STATUTS[statut] || ['neutre', statut];
  return <Badge ton={ton} point>{libelle}</Badge>;
}

export const LIBELLES_CONFIANCE = {
  CONFIRME_OFFICIEL: 'Confirmé (texte officiel)',
  CONFIRME_SOGAS: 'Confirmé par SOGAS',
  A_VALIDER_JURISTE: 'À confirmer par le juriste',
  A_VALIDER_DRH: 'À confirmer par la DRH',
  A_VALIDER_COMPTABILITE: 'À confirmer par la comptabilité',
  INTERDIT_DE_CALCUL: 'Non utilisable pour le calcul',
};
export function BadgeConfiance({ niveau }) {
  const ton = niveau?.startsWith('CONFIRME_') ? 'succes' : niveau === 'INTERDIT_DE_CALCUL' ? 'danger' : 'alerte';
  return <Badge ton={ton}>{LIBELLES_CONFIANCE[niveau] || niveau}</Badge>;
}

const FMT_HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Africa/Dakar', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
/** Heure de Dakar d'un instant (ISO ou Date) : « 08:00 ». */
export const heureDakar = (d) => (d ? FMT_HEURE.format(new Date(d)) : '');
/** Minutes → « 8 h 00 ». */
export const duree = (min) => (min == null ? '' : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`);
export const nomPersonne = (prenom, nom) => [prenom, nom].filter(Boolean).join(' ') || 'Non renseigné';
export const JOURS_SEMAINE = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
